"use client";

import { motion } from "framer-motion";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Camera } from "lucide-react";

const gallery = [
  { src: "/images/market-stall.png", title: "L'étal du marché", caption: "Racines fraîches en vente directe" },
  { src: "/images/hero-harvest.png", title: "La récolte dorée", caption: "Patates, ignames, manioc à l'heure dorée" },
  { src: "/images/patate-douce.png", title: "Patate douce", caption: "La reine dorée de notre maison" },
  { src: "/images/transformation-process.png", title: "L'atelier", caption: "Le savoir-faire en mouvement" },
  { src: "/images/attieke.png", title: "Attiéké prêt", caption: "Couscous de manioc traditionnel" },
  { src: "/images/farm-field.png", title: "Le champ", caption: "Nos terres fertiles au coucher du soleil" },
  { src: "/images/igname.png", title: "Igname royal", caption: "Le tubercule noble des tropiques" },
  { src: "/images/chips-patate.png", title: "Chips artisanales", caption: "Le snacking noble, sans artifice" },
];

export function Gallery() {
  return (
    <section id="galerie" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="divider-gold max-w-xs mx-auto mb-5"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-gold-deep font-semibold">
              Galerie
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
          >
            Une <span className="text-gradient-gold">féerie</span> de racines et de
            couleurs
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed"
          >
            Plongez dans l&apos;univers PATATE OR. Chaque image raconte un geste, une
            récolte, une transformation. La terre, l&apos;or, la main de l&apos;homme.
          </motion.p>
        </div>

        {/* Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-14"
        >
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {gallery.map((g, i) => (
                <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="group relative overflow-hidden rounded-2xl shadow-gold hover:shadow-gold-lg transition-all duration-500">
                    <div className="aspect-[4/5] overflow-hidden">
                      <img
                        src={g.src}
                        alt={g.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src = "/images/patate-douce.png";
                        }}
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-earth-deep/90 via-earth-deep/20 to-transparent opacity-90" />
                    <div className="absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-full glass-card">
                      <Camera className="w-4 h-4 text-cream" />
                    </div>
                    <div className="absolute bottom-5 left-5 right-5">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-gold-soft font-semibold">
                        {String(i + 1).padStart(2, "0")} / {gallery.length}
                      </div>
                      <h3 className="font-display text-xl font-bold text-cream mt-1">
                        {g.title}
                      </h3>
                      <p className="text-cream/70 text-sm italic">{g.caption}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2 bg-gradient-gold text-white border-0 hover:opacity-90" />
            <CarouselNext className="right-2 bg-gradient-gold text-white border-0 hover:opacity-90" />
          </Carousel>
        </motion.div>

        {/* Mosaic strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-10 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2"
        >
          {gallery.map((g, i) => (
            <div
              key={i}
              className="aspect-square rounded-lg overflow-hidden ring-1 ring-border hover:ring-gold transition-all cursor-pointer group"
              onClick={() => {
                const carousel = document.querySelector('[data-carousel]');
                carousel?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <img
                src={g.src}
                alt={g.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = "/images/patate-douce.png";
                }}
              />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
